import React, { useState } from "react";
// TODO: Implement custom API client
import { useQuery } from "@tanstack/react-query";
import TransactionFilters from "@/shared/components/transactions/TransactionFilters";
import TransactionTable from "@/shared/components/transactions/TransactionTable";
import TransactionDetail from "@/shared/components/transactions/TransactionDetail";
import { Button } from "@/shared/components/ui/button";
import { ChevronLeft, ChevronRight, ArrowDownToLine } from "lucide-react";
import { useLanguage } from "@/shared/components/shared/LanguageContext";

export default function Deposits() {
  const { t } = useLanguage();
  const [filters, setFilters] = useState({
    search: "",
    type: "all",
    status: "all",
    merchant: "all",
    provider: "all",
  });
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [page, setPage] = useState(0);
  const pageSize = 20;

  // TODO: Implement custom API calls
  const { data: transactions = [], isLoading } = useQuery({
    queryKey: ["deposits"],
    queryFn: async () => [], // TODO: Implement Transaction.filter (deposits) API call
  });

  const { data: merchants = [] } = useQuery({
    queryKey: ["merchants"],
    queryFn: async () => [], // TODO: Implement Merchant.list API call
  });

  const { data: providers = [] } = useQuery({
    queryKey: ["providers"],
    queryFn: async () => [], // TODO: Implement Provider.list API call
  });

  const filteredTransactions = React.useMemo(() => {
    return transactions.filter((tx) => {
      if (filters.search) {
        const search = filters.search.toLowerCase();
        if (
          !tx.transaction_id?.toLowerCase().includes(search) &&
          !tx.merchant_ref?.toLowerCase().includes(search)
        ) {
          return false;
        }
      }
      if (filters.type !== "all" && tx.type !== filters.type) return false;
      if (filters.status !== "all" && tx.status !== filters.status) return false;
      if (filters.merchant !== "all" && tx.merchant_id !== filters.merchant) return false;
      if (filters.provider !== "all" && tx.provider_id !== filters.provider) return false;
      return true;
    });
  }, [transactions, filters]);

  const paginatedTransactions = filteredTransactions.slice(
    page * pageSize,
    (page + 1) * pageSize
  );
  const totalPages = Math.ceil(filteredTransactions.length / pageSize);

  // Stats
  const stats = React.useMemo(() => {
    const success = filteredTransactions.filter((t) => t.status === "success");
    const pending = filteredTransactions.filter((t) => t.status === "pending" || t.status === "processing");
    return {
      total: success.reduce((sum, t) => sum + (t.amount || 0), 0),
      count: filteredTransactions.length,
      successCount: success.length,
      pendingCount: pending.length,
    };
  }, [filteredTransactions]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
              <ArrowDownToLine className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">{t("deposits")}</h1>
              <p className="text-slate-500">{stats.count} {t("deposits")} • ฿{stats.total.toLocaleString()} {t("total")}</p>
            </div>
          </div>
        </div>
        <div className="flex gap-4 text-sm">
          <div className="px-4 py-2 bg-emerald-50 rounded-lg">
            <span className="text-emerald-600 font-medium">{stats.successCount} {t("successful")}</span>
          </div>
          <div className="px-4 py-2 bg-amber-50 rounded-lg">
            <span className="text-amber-600 font-medium">{stats.pendingCount} {t("pending")}</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <TransactionFilters
        filters={filters}
        onFilterChange={(f) => {
          setFilters(f);
          setPage(0);
        }}
        merchants={merchants}
        providers={providers}
        onReset={() => {
          setFilters({ search: "", type: "all", status: "all", merchant: "all", provider: "all" });
          setPage(0);
        }}
      />

      {/* Table */}
      <TransactionTable
        transactions={paginatedTransactions}
        isLoading={isLoading}
        onView={setSelectedTransaction}
      />

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-slate-500">
            Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, filteredTransactions.length)} of {filteredTransactions.length}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage(page - 1)} disabled={page === 0}>
              <ChevronLeft className="h-4 w-4" />
            </Button>
            <span className="text-sm">Page {page + 1} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1}>
              <ChevronRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      )}

      {/* Detail Sheet */}
      <TransactionDetail
        transaction={selectedTransaction}
        open={!!selectedTransaction}
        onClose={() => setSelectedTransaction(null)}
      />
    </div>
  );
}